import { FC, lazy, useEffect, useState } from "react";
import { Dictionary, HttpStatusCode, replaceStringVariables } from "aphrodite.react/Helpers";
import { AppContext } from "Context/AppContext";
import { ConfigurationClient, ConfigurationGetResponse, LanguageClient, LanguageGetResponse, ResourceClient, SessionClient, SessionGetResponse } from "AdminApi/AdminApi";
import { Layout } from "./Layout";
import { Navigate, Route, Routes } from "react-router";
import { Language } from "Helpers/Language";
import { useBusy } from "aphrodite.react/Hooks";

const LoginLazy = lazy(() => import("Pages/Login"));
const ResetPasswordLazy = lazy(() => import("Pages/ResetPassword/ResetPassword"));
const AdminAreaLazy = lazy(() => import("Pages/Admin/AdminArea"));

export const App: FC = () => {
    const [resources, setResources] = useState<Dictionary<string> | null>(null);
    const [session, setSession] = useState<SessionGetResponse | null | undefined>(null);
    const [languages, setLanguages] = useState<LanguageGetResponse[] | null>(null);
    const [configuration, setConfiguration] = useState<ConfigurationGetResponse | null>(null);
    const { isBusy, busyWhile } = useBusy();

    const getResources = async () => {
        const languageId = await Language.get();
        const response = await new ResourceClient().get(languageId);
        if (response.status === HttpStatusCode.Http200Ok) {
            setResources(response.result);
        }
    };

    const refreshSession = async () => {
        const response = await new SessionClient().get();

        switch (response.status) {
            case HttpStatusCode.Http200Ok:
                setSession(response.result);
                break;
            case HttpStatusCode.Http204NoContent:
                setSession(undefined);
                break;
        }

        return Promise.resolve();
    };

    const refreshLanguages = async () => {
        const response = await new LanguageClient().getAll();

        if (response.status === HttpStatusCode.Http200Ok) {
            setLanguages(response.result);
        }
    };

    const getConfiguration = async () => {
        const response = await new ConfigurationClient().get();
        if (response.status === HttpStatusCode.Http200Ok) {
            setConfiguration(response.result);
        }
    };

    const resource = (key: string, variables?: Dictionary<string | number>) => {
        if (resources) {
            const result = resources[key];

            if (result) {
                return replaceStringVariables(result, variables);
            }
        }

        return `{{${key}}}`;
    };

    useEffect(() => {
        getResources();
        refreshSession();
        refreshLanguages();
        getConfiguration();
    }, []);

    if (!resources) return null;

    return (
        <AppContext.Provider value={{ resource, session, refreshSession, languages, refreshLanguages, configuration, isBusy, busyWhile }}>
            <Layout>
                <Routes>
                    <Route index element={<Navigate to="admin" />} />
                    <Route path="login">
                        <Route index path="*" element={<LoginLazy />} />
                    </Route>
                    <Route path="resetpassword">
                        <Route index path="*" element={<ResetPasswordLazy />} />
                    </Route>
                    <Route path="admin">
                        <Route index path="*" element={<AdminAreaLazy />} />
                    </Route>
                </Routes>
            </Layout>
        </AppContext.Provider>
    );
};